import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { Box, Button, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Form, useFormikContext } from 'formik';
import React, { useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

import { useHistory, useLocation } from 'react-router-dom';
import { CHOOSE_OPTIONS_TO_CONTINUE_TEXT } from '../../constants/marketplace';
import { SCROLL_LANDING_OFFSET_DESKTOP, SCROLL_LANDING_OFFSET_MOBILE } from '../../constants/ui';
import { useFab } from '../../hooks/useFab';
import { useFooter } from '../../hooks/useFooter';
import { selectIsFirstStep } from '../../store/selectors/derived';
import { selectIsDigital, selectIsEmbed } from '../../store/selectors/init';
import { selectHasMissingVariantPicks } from '../../store/selectors/marketplace';
import { IFixedAddress } from '../../types/api';
import { ICustomOrFixedAddressForm } from '../../types/shipping';
import { AddressChoiceEnum } from '../../types/ui';
import { getConvertedPathname } from '../../util/helpers';
import AddressFormContainer from './AddressFormContainer';
import ContactFormContainer from './ContactFormContainer';

interface IShippingFormContainerProps {
  addressBook: IFixedAddress[] | undefined;
  fixedAddress: IFixedAddress[] | undefined;
}

export const ShippingFormContainer: React.FC<IShippingFormContainerProps> = ({ addressBook, fixedAddress }) => {
  const history = useHistory();
  const { pathname } = useLocation();
  const theme = useTheme();
  const isMobileViewport = useMediaQuery(theme.breakpoints.down('sm'));

  const isDigital = useSelector(selectIsDigital);
  const isEmbed = useSelector(selectIsEmbed);
  const isFirstStep = useSelector(selectIsFirstStep(getConvertedPathname(pathname)));
  const hasMissingVariantPicks = useSelector(selectHasMissingVariantPicks);

  // The form section itself is the sticky bar's anchor — the shipping page's "selection
  // section" is the form heading/body.
  const formSectionRef = useRef<HTMLDivElement>(null);

  const { isSubmitting, isValid, submitForm, errors, values } = useFormikContext<ICustomOrFixedAddressForm>();

  const scrollToForm = useCallback(
    (behavior: ScrollBehavior) => {
      const section = formSectionRef.current;
      if (!section) {
        return;
      }
      const top = section.getBoundingClientRect().top + window.scrollY;
      const offset = isMobileViewport ? SCROLL_LANDING_OFFSET_MOBILE : SCROLL_LANDING_OFFSET_DESKTOP;
      window.scrollTo({ top: top - offset, behavior });
    },
    [isMobileViewport],
  );

  const fabConfig = useMemo(
    () => ({
      down: { label: 'Go to shipping details', bannerLabel: 'Shipping details', onActivate: scrollToForm },
      up: {
        label: 'Back to top',
        onActivate: (behavior: ScrollBehavior) => window.scrollTo({ top: 0, behavior }),
      },
      desktopFabEnabled: true,
    }),
    [scrollToForm],
  );

  useFab(fabConfig);

  const isBookMode = values.mode === AddressChoiceEnum.Select;
  const errorKeys = useMemo(() => Object.keys(errors), [errors]);
  const onlyMissingAddressPick =
    isBookMode && errorKeys.length > 0 && errorKeys.every((key) => key === 'picked_address_id');

  const helperCopy = useMemo(() => {
    if (hasMissingVariantPicks) {
      return CHOOSE_OPTIONS_TO_CONTINUE_TEXT;
    }
    if (isValid) {
      return undefined;
    }

    return onlyMissingAddressPick ? 'Select a delivery address to confirm' : 'Fill required fields to confirm';
  }, [isValid, onlyMissingAddressPick, hasMissingVariantPicks]);

  const footerConfig = useMemo(
    () => ({
      backButton: isFirstStep ? undefined : (
        <Button variant="outlined" color="primary" startIcon={<ChevronLeftIcon />} onClick={() => history.goBack()}>
          Back
        </Button>
      ),
      helperText: helperCopy ? (
        <Typography variant={isMobileViewport ? 'body2' : 'body1'} color="text.secondary">
          {helperCopy}
        </Typography>
      ) : undefined,
      submitButton: (
        <Button
          variant="contained"
          color="primary"
          disabled={isSubmitting || !isValid || hasMissingVariantPicks}
          fullWidth={isMobileViewport}
          size="medium"
          onClick={() => !isEmbed && submitForm()}
        >
          Confirm & send
        </Button>
      ),
    }),
    [
      isEmbed,
      isSubmitting,
      isMobileViewport,
      isValid,
      hasMissingVariantPicks,
      submitForm,
      isFirstStep,
      helperCopy,
      history,
    ],
  );

  useFooter(footerConfig, formSectionRef);

  return (
    <Form style={{ width: '100%' }}>
      <Box ref={formSectionRef} sx={{ display: 'flex', flexDirection: 'column' }}>
        <ContactFormContainer />
        {!isDigital && <AddressFormContainer addressBook={addressBook} fixedAddress={fixedAddress} />}
      </Box>
    </Form>
  );
};
